<template>
  <!--
    Design refresh phase 2, surface 4 (the shell).

    Two of the four defects the plan's surface-4 row names lived in this file:
    "a green avatar bubble, a hardcoded 'Online' that is always true"
    (refresh_plan.md section 4). audit_web.md 6.1 gives the lines:
    navUser.vue:6, :46 and :54.

    The status line is GONE rather than restyled. There is no presence model in
    this product — nothing sets it, nothing polls it, nothing could — so
    `<span>&bull;</span>Online` was a claim the app was in no position to make,
    rendered in `color: green`, a CSS named colour. Restyling it would have
    produced an honest-looking lie. The three axes this refresh is built on
    (custody, telemetry, sync) are all about refusing to paint a state the
    product does not actually know, and a user's presence is one more of those.

    The initials are aria-hidden because the user's full name is rendered
    immediately beside them: without it a screen reader announces "JD John
    Doe".
  -->
  <div v-if="user.name" ref="root" class="lw-p-nav-user" @focusout="onFocusOut">
    <!--
      Spec 53 15.4 (Mark, 2026-11-02): Training left the header on production
      and lives here, under your name. When training is offered to you the name
      is a button that opens a small menu; when it is not, the block is the
      plain name it always was, because a button that opens an empty menu is a
      promise with nothing behind it.

      A disclosure (a button with aria-expanded and the list it controls), not
      an ARIA menu: one link is not worth the arrow-key contract role="menu"
      makes, and a link in a list is something every screen reader already
      knows how to use. Escape, a click outside and a Tab out close it; opening
      it puts focus on the item, Escape puts focus back on the name.
    -->
    <button
      v-if="menuOn"
      ref="trigger"
      type="button"
      class="lw-p-nav-user__face lw-p-nav-user__face--button"
      :aria-expanded="open ? 'true' : 'false'"
      :aria-controls="menuId"
      data-test="nav-user-button"
      @click="toggle"
      @keydown.esc="close(true)"
    >
      <!--
        A collapsed rail is icons only, so the bubble is all there is to press.
        A name it cannot make initials from gets a person glyph rather than no
        bubble, or the collapsed button would have nothing to see.
      -->
      <span class="lw-p-nav-user__bubble" aria-hidden="true"
        >{{ shortName }}<i v-if="!shortName" class="fa fa-user"
      /></span>
      <span class="lw-p-nav-user__details">
        <span class="lw-p-nav-user__name">{{ user.name }}</span>
      </span>
      <i class="fa fa-angle-down lw-p-nav-user__caret" aria-hidden="true" />
    </button>
    <div v-else class="lw-p-nav-user__face">
      <!--
        v-if, not just an empty-string render: `shortName` still returns '' for
        a 3+ word name (see the computed below), and an empty span inside a
        circle of solid colour is a visible blank dot, not a blank name. Hiding
        the bubble is the cheap, honest fix; inventing a name-parsing rule for
        the 3+ word case was explicitly out of scope for this surface.
      -->
      <span v-if="shortName" class="lw-p-nav-user__bubble" aria-hidden="true">{{ shortName }}</span>
      <div class="lw-p-nav-user__details">
        <span class="lw-p-nav-user__name">{{ user.name }}</span>
      </div>
    </div>

    <ul
      v-if="menuOn && open"
      :id="menuId"
      class="lw-p-nav-user__menu"
      data-test="nav-user-menu"
      @keydown.esc="close(true)"
    >
      <li>
        <router-link
          ref="training"
          class="lw-p-nav-user__item"
          data-test="nav-user-training"
          :to="{ name: 'Training' }"
          @click="close(false)"
          >Training</router-link
        >
      </li>
    </ul>
  </div>
</template>

<script>
let uid = 0;

export default {
  props: {
    user: {
      type: Object,
      required: true,
    },
  },
  data() {
    uid += 1;
    return { open: false, menuId: `lw-nav-user-menu-${uid}` };
  },
  computed: {
    // Spec 53 15.4: the condition the header's Training button had (offered
    // to this ungated user, never on the anonymous demo), and never inside a
    // practice fleet, whose header keeps its own Training link.
    menuOn() {
      const getters = (this.$store && this.$store.getters) || {};
      return Boolean(getters.trainingOffered) && !getters.isDemo && !getters.trainingLease;
    },
    shortName() {
      const name = this.user.name.split(' ');
      switch (name.length) {
        case 1:
          return name[0].substr(0, 1);
        case 2:
          return name[0].substr(0, 1) + name[1].substr(0, 1);
        default:
          return '';
      }
    },
  },
  watch: {
    // Following the link, or any other navigation, leaves the menu shut.
    '$route.fullPath'() {
      this.open = false;
    },
  },
  mounted() {
    document.addEventListener('pointerdown', this.onOutside, true);
  },
  beforeUnmount() {
    document.removeEventListener('pointerdown', this.onOutside, true);
  },
  methods: {
    toggle() {
      if (this.open) {
        this.close(false);
        return;
      }
      this.open = true;
      this.$nextTick(() => {
        const link = this.$refs.training;
        const el = link && (link.$el || link);
        if (el && el.focus) el.focus({ preventScroll: true });
      });
    },
    /** Shut the menu; `refocus` puts focus back on the name (Escape). */
    close(refocus) {
      if (!this.open) return;
      this.open = false;
      if (!refocus) return;
      this.$nextTick(() => {
        if (this.$refs.trigger) this.$refs.trigger.focus({ preventScroll: true });
      });
    },
    onOutside(event) {
      if (!this.open) return;
      const root = this.$refs.root;
      if (root && root.contains(event.target)) return;
      this.open = false;
    },
    // Tabbing past the item leaves the menu shut: one left open behind the
    // focus is a menu nobody can tell they are still in. A focusout with no
    // relatedTarget is a click on nothing focusable, which onOutside handles.
    onFocusOut(event) {
      if (!this.open) return;
      const next = event.relatedTarget;
      const root = this.$refs.root;
      if (!next || (root && root.contains(next))) return;
      this.open = false;
    },
  },
};
</script>

<style scoped>
/*
  Scoped, where every block in this directory used to be global. `#sidebar` is
  an ancestor selector rather than one of this component's own elements, and
  that still works: Vue appends the scope attribute to the LAST compound
  selector, so `#sidebar.collapsed .lw-p-nav-user` becomes
  `#sidebar.collapsed .lw-p-nav-user[data-v-…]` and matches this component's
  root exactly as before.

  Every class below is renamed into the lw-p- page namespace
  (`#nav-user-content` -> `.lw-p-nav-user`, `#nav-user-bubble` ->
  `.lw-p-nav-user__bubble`, and so on). `#sidebar` itself stays an id: it is
  one of the four shell ids (`#sidebar`, `#app-title`, `#nav-user`,
  `#nav-list`) this surface is told to reference as an ancestor rather than
  restyle, and an ancestor selector is not "a class this file declares".
*/
/* The positioning context for the Training menu (spec 53 15.4). */
.lw-p-nav-user {
  position: relative;
}

.lw-p-nav-user__face {
  align-items: center;
  display: flex;
  min-width: 11rem;
}

/*
  The name as a button: a plain block that happens to be pressable, so it
  keeps the face's look and gains only a cursor, a caret and the focus ring.
  The ring sits outside the block, which has the rail's padding around it.
*/
.lw-p-nav-user__face--button {
  appearance: none;
  background: none;
  border: 0;
  border-radius: var(--lw-radius-md);
  color: inherit;
  cursor: pointer;
  font: inherit;
  padding: 0;
  text-align: left;
  width: 100%;
}

.lw-p-nav-user__face--button:focus-visible {
  outline: var(--lw-focus-ring-width) solid var(--lw-focus-ring-color);
  outline-offset: var(--lw-focus-ring-offset);
}

.lw-p-nav-user__face--button:focus:not(:focus-visible) {
  outline: none;
}

.lw-p-nav-user__caret {
  margin-left: auto;
  opacity: 0.62;
  padding-left: var(--lw-space-2);
}

.lw-p-nav-user__face--button:hover .lw-p-nav-user__caret,
.lw-p-nav-user__face--button[aria-expanded='true'] .lw-p-nav-user__caret {
  opacity: 1;
}

/*
  A popover on the light elevated surface, the same treatment as the
  walkthrough menu's panel (LwTourMenu.vue), dropped under the name in a wide
  rail and beside the bubble in a narrow one (below). Absolute is safe here:
  the desk rail is deliberately not a scroll container (app.scss, the note on
  #sidebar), and the phone sheet scrolls rather than clips.
*/
.lw-p-nav-user__menu {
  background: var(--lw-elev-3-surface);
  border: 1px solid var(--lw-elev-3-line);
  border-radius: var(--lw-radius-md);
  box-shadow: var(--lw-elev-3-shadow);
  left: 0;
  list-style: none;
  margin: 0;
  min-width: 10rem;
  padding: var(--lw-space-1);
  position: absolute;
  right: 0;
  top: calc(100% + var(--lw-space-1));
  z-index: var(--lw-z-overlay);
}

.lw-p-nav-user__item {
  border-radius: var(--lw-radius-sm);
  color: var(--lw-fg-primary);
  display: block;
  font: var(--lw-type-body);
  padding: var(--lw-space-2) var(--lw-space-3);
  text-decoration: none;
}

.lw-p-nav-user__item:hover,
.lw-p-nav-user__item:focus-visible {
  background-color: var(--lw-bg-inset);
  color: var(--lw-fg-primary);
  text-decoration: none;
}

.lw-p-nav-user__item:focus-visible {
  outline: var(--lw-focus-ring-width) solid var(--lw-focus-ring-color);
  outline-offset: calc(var(--lw-focus-ring-offset) * -1);
}

/*
  The bubble was `#13b36d`, a green that appears nowhere else in the brand
  (audit_web.md 1.2). It is not merely off-palette: after phase 0, green means
  exactly one thing in this system — custody confirmed by a human at the
  office — and the token file is explicit that it is spent once. An avatar is
  the last thing that should be spending it.

  Navy 800 is the ramp step that reads as raised off the chassis without
  becoming a second accent, and the plate face in caps is the SIGNALWORKS
  treatment for a short all-caps mark.
*/
.lw-p-nav-user__bubble {
  align-items: center;
  background-color: var(--lw-navy-800);
  color: var(--lw-fg-inverse);
  border-radius: var(--lw-radius-pill);
  display: flex;
  flex: none;
  justify-content: center;
  width: 3em;
  height: 3em;
  font: var(--lw-type-label);
  /* The label role's tracking is deliberately NOT applied here. It exists to
     open up a run of caps in a line of text; inside a 3em circle holding two
     glyphs it only pushes the pair off centre. */
  text-transform: var(--lw-type-label-transform);
}

.lw-p-nav-user__name {
  font: var(--lw-type-body-sm);
}

#sidebar.collapsed .lw-p-nav-user__face {
  justify-content: center;
  min-width: 0;
}

#sidebar.collapsed .lw-p-nav-user__caret {
  display: none;
}

/* Beside the icon strip rather than under it, where a 4.5rem rail has no room
   for a menu. The offset clears #nav-user's own padding to the rail's edge. */
#sidebar.collapsed .lw-p-nav-user__menu {
  left: calc(100% + var(--lw-space-4) + var(--lw-space-2));
  right: auto;
  top: 0;
}

#sidebar.expanded .lw-p-nav-user__details {
  display: flex;
  flex-direction: column;
  padding-left: var(--lw-space-4);
}

/* Visually hidden rather than display: none, so the name still names the
   button in the icon strip (and is still read beside the bubble when there is
   no button). The clip-rect shape sidebar.vue's h1 uses. */
#sidebar.collapsed .lw-p-nav-user__details {
  border: 0;
  clip: rect(0 0 0 0);
  height: 1px;
  margin: -1px;
  overflow: hidden;
  padding: 0;
  position: absolute;
  white-space: nowrap;
  width: 1px;
}
</style>
